import { ReactNode } from 'react'
import type { DescriptionChange } from '../../../api/beslutsstod/beslutsstod'
import { MunicipalityData } from '../types/municipalityData'

const getFrequencyValueOther = (taskName: string, frequency: number | undefined, municipalityData: MunicipalityData, weeksPerMonth: number, selection: string) => {
  if (selection === '-') return undefined

  const correspondingTask = municipalityData.input.extraTasks.find((task) => task.name === taskName)

  // if there are no frequencies for this task, and the frequency was 1, we assume that the level has a normalized standardTime for the month
  if (correspondingTask?.frequencies?.length === 0) {
    return frequency === 1 ? undefined : frequency
  }

  if (!frequency) return undefined

  return Math.round((1000 * frequency) / weeksPerMonth) / 1000
}

const getTimepointFrequencyValue = (frequency: number | undefined, selection: string) => {
  if (selection === '-') return undefined

  return frequency || undefined
}

// Varningarnas texter (#79): först vad det gäller, avslutat med punkt, sedan vad som är fel.
// Bara det som är fel är fetstilt. Tidpunkten skrivs före insatsen när insatsen ligger på en tidpunkt.
const taskLabel = (taskName: string, timepoint?: string) => (timepoint ? `${timepoint.trim()} – ${taskName.trim()}` : taskName.trim())

const frequencyLabel = (timesPerWeek: number) => `${timesPerWeek.toLocaleString('sv-SE', { maximumFractionDigits: 3 })} ${timesPerWeek === 1 ? 'gång' : 'gånger'} i veckan`

export interface JSONLoadError {
  message: ReactNode
  errorType: 'exists' | 'frequency' | 'selection' | 'exclusive' | 'maxAllocations' | 'description'
  taskType?: 'other' | 'timepoint'
  taskName?: string
  timepoint?: string
}

const getErrorsForSingleOtherTask = (taskName: string, taskData: any, municipalityData: MunicipalityData, weeksPerMonth: number) => {
  const errors: JSONLoadError[] = []
  const frequencyValue = getFrequencyValueOther(taskName, taskData.frequency, municipalityData, weeksPerMonth, taskData.selection)

  // Make sure all extraTasks 1. exists, 2. have a frequency that exists, 3. have a selection that exists

  // 1. exists
  if (!municipalityData.input.extraTasks.find((t) => t.name === taskName)) {
    errors.push({
      message: (
        <>
          <b>{taskLabel(taskName)}</b>. Insatsen finns inte längre och har ignorerats vid kopiering.
        </>
      ),
      errorType: 'exists',
      taskName,
      taskType: 'other',
    })
    return errors
  }

  // 2. have a frequency that exists

  if (frequencyValue !== undefined || taskData.frequency !== 1) {
    const correspondingTask = municipalityData.input.extraTasks.find((t) => t.name === taskName)

    if (correspondingTask?.frequencies?.findIndex((f) => f.timesPerWeek === frequencyValue) === -1) {
      errors.push({
        message: (
          <>
            {taskLabel(taskName)}
            {frequencyValue !== undefined && (
              <>
                , <b>{frequencyLabel(frequencyValue)}</b>
              </>
            )}
            . Frekvensen finns inte längre och har ignorerats vid kopiering.
          </>
        ),
        errorType: 'frequency',
        taskName,
        taskType: 'other',
      })
    }
  }
  // 3. have a selection that exists
  if (taskData.selection !== '-' && !municipalityData.input.extraTasks.find((t) => t.name === taskName)?.selections.includes(taskData.selection)) {
    errors.push({
      message: (
        <>
          {taskLabel(taskName)}, <b>{taskData.selection}</b>. Nivån finns inte längre och har ignorerats vid kopiering.
        </>
      ),
      errorType: 'selection',
      taskName,
      taskType: 'other',
    })
  }

  return errors
}

const getErrorsForSingleTimepointTask = (timepoint: string, taskName: string, taskData: any, municipalityData: MunicipalityData) => {
  const errors: JSONLoadError[] = []
  const frequencyValue = getTimepointFrequencyValue(taskData.frequency, taskData.selection)

  // Make sure all timepointTasks 1. have timepoints that exists, 2. have a frequency that exists, 3. have a selection that exists

  // 1. exists
  if (municipalityData.input.timepointTasks.findIndex((t) => t.name === taskName) === -1) {
    errors.push({
      message: (
        <>
          <b>{taskLabel(taskName)}</b>. Insatsen finns inte längre och har ignorerats vid kopiering.
        </>
      ),
      errorType: 'exists',
      taskName,
      taskType: 'timepoint',
      timepoint,
    })
    return errors
  }

  // 2. have a frequency that exists

  if (frequencyValue !== undefined) {
    const correspondingTask = municipalityData.input.timepointTasks.find((t) => t.name === taskName)

    if (correspondingTask?.frequencies?.findIndex((f) => f.timesPerWeek === frequencyValue) === -1) {
      errors.push({
        message: (
          <>
            {taskLabel(taskName, timepoint)}, <b>{frequencyLabel(frequencyValue)}</b>. Frekvensen finns inte längre och har ignorerats vid kopiering.
          </>
        ),
        errorType: 'frequency',
        taskName,
        taskType: 'timepoint',
        timepoint,
      })
    }
  }

  // 3. have a selection that exists
  if (taskData.selection !== '-' && !municipalityData.input.timepointTasks.find((t) => t.name === taskName)?.selections.includes(taskData.selection)) {
    errors.push({
      message: (
        <>
          {taskLabel(taskName, timepoint)}, <b>{taskData.selection}</b>. Nivån finns inte längre och har ignorerats vid kopiering.
        </>
      ),
      errorType: 'selection',
      taskName,
      taskType: 'timepoint',
      timepoint,
    })
  }

  return errors
}

// En insats som ignoreras på en tidpunkt finns inte kvar att rätta. Varningar om dess nivå eller
// frekvens där ska därför inte visas, bara att den ignorerats (#79).
const withoutFrequencyAndSelectionErrors = (errors: JSONLoadError[], timepoint: string, taskName: string) =>
  errors.filter((error) => !(error.taskType === 'timepoint' && error.timepoint === timepoint && error.taskName === taskName && (error.errorType === 'frequency' || error.errorType === 'selection')))

const errorTypeByField: Record<string, JSONLoadError['errorType']> = { Besök: 'frequency', Frekvens: 'frequency', Nivå: 'selection' }

// Den röda ramen på en nivå eller frekvens som inte gick att läsa in försvinner när just det fältet
// ändras, så att det som är kvar att rätta syns (#79). changedValues är formulärets onValuesChange.
export const withoutErrorsForChangedFields = (errors: JSONLoadError[], changedValues: Record<string, any>) =>
  errors.filter((error) => {
    if (!error.taskName || (error.errorType !== 'frequency' && error.errorType !== 'selection')) return true

    const changedFields = error.taskType === 'other' ? changedValues.other?.[error.taskName] : error.timepoint ? changedValues[error.timepoint]?.timepoints?.[error.taskName] : undefined
    return !(changedFields && Object.keys(changedFields).some((field) => errorTypeByField[field] === error.errorType))
  })

const getErrorsForTimepoint = (timepoint: string, timepointData: any, municipalityData: MunicipalityData, weeksPerMonth: number) => {
  const errors: JSONLoadError[] = []

  if (!municipalityData.input.timepoints.includes(timepoint)) {
    if (Object.keys(timepointData).length === 0) {
      return [] as JSONLoadError[]
    }

    errors.push({
      message: (
        <>
          <b>{timepoint.trim()}</b>. Tidpunkten finns inte längre och har ignorerats vid kopiering.
        </>
      ),
      errorType: 'exists',
    })
  }
  return errors
}

function jsonLoader(data: Record<string, any>, municipalityData: MunicipalityData) {
  const weeksPerMonth = data.weeksPerMonth ?? 4

  const result: any = {
    WeeksOff: data.weeksOff ?? 0,
    shared: data.shared ?? false,
    other: {},
    checkboxes: {},
  }

  let errors: JSONLoadError[] = []

  for (const key of Object.keys(data)) {
    if (key === 'extraTasks') {
      const task = data[key]
      for (const item in task) {
        let frequencyValue = getFrequencyValueOther(item, task[item].frequency, municipalityData, weeksPerMonth, task[item].selection)
        let level = task[item].selection !== '-' ? task[item].selection : undefined
        if (!level && frequencyValue === undefined) {
          continue
        }

        const taskErrors = getErrorsForSingleOtherTask(item, task[item], municipalityData, weeksPerMonth)
        if (taskErrors.length) {
          errors.push(...taskErrors)
          if (taskErrors.find((error) => error.errorType === 'exists')) {
            continue
          }
          if (taskErrors.find((error) => error.errorType === 'frequency')) {
            frequencyValue = undefined
          }
          if (taskErrors.find((error) => error.errorType === 'selection')) {
            level = undefined
          }
        }

        result.other[item] = {
          Frekvens: frequencyValue,
          Nivå: level,
          Dubbelbemanning: task[item].double ?? false,
        }
      }
    } else if (key === 'timepointTasks') {
      for (const timepoint in data.timepointTasks) {
        const timepointErrors = getErrorsForTimepoint(timepoint, data.timepointTasks[timepoint], municipalityData, weeksPerMonth)

        result[timepoint] = { timepoints: {} }
        let allTaskErrors: JSONLoadError[] = []
        for (const task in data.timepointTasks[timepoint]) {
          let frequencyValue = getTimepointFrequencyValue(data.timepointTasks[timepoint][task].frequency, data.timepointTasks[timepoint][task].selection)
          let level = data.timepointTasks[timepoint][task].selection !== '-' ? data.timepointTasks[timepoint][task].selection : undefined

          if (!level && frequencyValue === undefined) {
            continue
          }

          const taskErrors = getErrorsForSingleTimepointTask(timepoint, task, data.timepointTasks[timepoint][task], municipalityData)
          if (taskErrors.length) {
            allTaskErrors.push(...taskErrors)
            if (taskErrors.find((error) => error.errorType === 'exists')) {
              continue
            }
            if (taskErrors.find((error) => error.errorType === 'frequency')) {
              frequencyValue = undefined
            }
            if (taskErrors.find((error) => error.errorType === 'selection')) {
              level = undefined
            }
          }

          result[timepoint].timepoints[task] = {
            Besök: frequencyValue,
            Nivå: level,
            Dubbelbemanning: data.timepointTasks[timepoint][task].double,
          }
        }

        // En insats som ska vara ensam på tidpunkten kan inte läsas in tillsammans med andra.
        // Den ignoreras, så att övriga insatser kan behållas.
        if (Object.keys(result[timepoint].timepoints).length > 1) {
          for (const taskName of Object.keys(result[timepoint].timepoints)) {
            if (!municipalityData.input.timepointTasks.find((t) => t.name === taskName)?.exclusive) continue

            allTaskErrors = withoutFrequencyAndSelectionErrors(allTaskErrors, timepoint, taskName)
            allTaskErrors.push({
              message: (
                <>
                  {timepoint.trim()} – <b>{taskName.trim()}</b>. Insatsen kan inte kombineras med andra insatser på samma tidpunkt och har ignorerats vid kopiering.
                </>
              ),
              errorType: 'exclusive',
              taskName,
              taskType: 'timepoint',
              timepoint,
            })
            delete result[timepoint].timepoints[taskName]
          }
        }

        // Only show timepoint errors if there were any and we have atleast one non-ignored timepoint task (all will get exists error if timepoint doesn't exist).
        if (timepointErrors.length && Object.keys(data.timepointTasks[timepoint]).length !== 0) {
          errors.push(...timepointErrors)
          // In this case delete timepoint entry
          delete result[timepoint]
          continue
        }

        // We want to show task errors if there is no timepoint error and there are task errors
        if (allTaskErrors.length && timepointErrors.length === 0) {
          errors.push(...allTaskErrors)
        }
      }
    }
  }

  // En insats som bara får väljas på ett visst antal tidpunkter behåller de första, i den ordning
  // tidpunkterna står i inställningarna. Övriga ignoreras, som en insats som ska vara ensam (#79).
  for (const task of municipalityData.input.timepointTasks) {
    if (task.maxAllocations == null) continue

    const allocatedTimepoints = municipalityData.input.timepoints.filter((timepoint) => {
      const taskData = result[timepoint]?.timepoints?.[task.name]
      return taskData && (taskData.Besök != null || taskData.Nivå != null)
    })

    for (const timepoint of allocatedTimepoints.slice(task.maxAllocations)) {
      errors = withoutFrequencyAndSelectionErrors(errors, timepoint, task.name)
      errors.push({
        message: (
          <>
            {timepoint.trim()} – <b>{task.name.trim()}</b>. Insatsen kan bara väljas på {task.maxAllocations === 1 ? 'en tidpunkt' : `${task.maxAllocations} tidpunkter`} och har ignorerats vid
            kopiering.
          </>
        ),
        errorType: 'maxAllocations',
        taskName: task.name,
        taskType: 'timepoint',
        timepoint,
      })
      delete result[timepoint].timepoints[task.name]
    }
  }

  // En insats som inte längre finns nämns en gång, även om den låg på flera tidpunkter (#79)
  errors = errors.filter((error, index, list) => error.errorType !== 'exists' || !error.taskName || list.findIndex((e) => e.errorType === 'exists' && e.taskName === error.taskName) === index)

  return { result, errors }
}

// Beskrivningar i ⓘ som ändrats sedan beräkningen sparades (#79). Servern jämför; här blir de varningar.
// Besök där insatsen inte lästes in (till exempel ignorerades) tas bort, liksom ändringar utan besök kvar.
export const getDescriptionChangeErrors = (changes: DescriptionChange[], result: Record<string, any>): JSONLoadError[] =>
  changes.flatMap((change) => {
    const timepoints = change.timepoints.filter((timepoint) => result[timepoint]?.timepoints?.[change.taskName])
    if (change.taskType === 'timepoint' ? timepoints.length === 0 : !result.other?.[change.taskName]) return []

    const timepointLabel = timepoints.join(', ')
    return [
      {
        message:
          change.kind === 'selection' ? (
            <>
              {taskLabel(change.taskName, timepointLabel || undefined)}, <b>{change.selectionName}</b>. Nivåns beskrivning har ändrats – kontrollera.
            </>
          ) : (
            <>
              {timepointLabel && `${timepointLabel} – `}
              <b>{change.taskName.trim()}</b>. Insatsens beskrivning har ändrats – kontrollera.
            </>
          ),
        errorType: 'description' as const,
        taskName: change.taskName,
        taskType: change.taskType,
      },
    ]
  })

export default jsonLoader
